import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import { fetchIncentiveReports } from '../../services/incentiveTicketsService';
import { formatOMR } from '../../utils/formatCurrency';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const STATUS_COLORS_MAP = {
  'Draft': '#6b7280',
  'Pending Team Assignment': '#f59e0b',
  'Calculated': '#3b82f6',
  'Pending Approval': '#f97316',
  'Approved': '#22c55e',
  'On Hold': '#eab308',
  'Ready for Payment': '#a855f7',
  'Paid': '#10b981',
  'Rejected': '#ef4444',
  'Cancelled': '#9ca3af',
};

const IncentiveReports = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchIncentiveReports();
      setReport(data);
    } catch (err) {
      setError(err?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const statusChartData = report?.byStatus
    ? Object.entries(report?.byStatus)?.map(([status, count]) => ({ status, count }))
    : [];

  const pieData = statusChartData?.filter(d => d?.count > 0);

  const kpiCards = [
    { label: 'Total Tickets', value: report?.total || 0, icon: 'Ticket', color: 'from-primary to-accent', format: 'number' },
    { label: 'Total Incentives Paid', value: report?.totalIncentivesPaid || 0, icon: 'Banknote', color: 'from-success to-emerald-600', format: 'currency' },
    { label: 'Total Sales Incentives', value: report?.totalSalesIncentives || 0, icon: 'TrendingUp', color: 'from-blue-500 to-blue-600', format: 'currency' },
    { label: 'Total Project Incentives', value: report?.totalProjectIncentives || 0, icon: 'Users', color: 'from-purple-500 to-purple-600', format: 'currency' },
  ];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="w-full max-w-7xl mx-auto space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Incentive Reports
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Overview of all incentive ticket activity</p>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-3 p-4 bg-error/10 border border-error/30 rounded-xl text-error text-sm">
            <Icon name="AlertCircle" size={16} />
            {error}
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {kpiCards?.map(card => (
            <div key={card?.label} className="bg-card border border-border rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className={`p-2.5 rounded-xl bg-gradient-to-br ${card?.color}`}>
                  <Icon name={card?.icon} size={18} color="white" />
                </div>
                <span className="text-sm text-muted-foreground">{card?.label}</span>
              </div>
              <div className="text-2xl font-bold text-foreground">
                {card?.format === 'currency' ? formatOMR(card?.value) : card?.value}
              </div>
            </div>
          ))}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Status Distribution Bar Chart */}
          <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
            <h2 className="font-semibold text-foreground mb-4">Tickets by Status</h2>
            {statusChartData?.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No data available</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={statusChartData} margin={{ top: 5, right: 10, left: -20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis
                    dataKey="status"
                    tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }}
                    angle={-35}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '12px', fontSize: '12px' }}
                    labelStyle={{ color: 'var(--color-foreground)' }}
                  />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                    {statusChartData?.map((entry) => (
                      <Cell key={entry?.status} fill={STATUS_COLORS_MAP?.[entry?.status] || '#6b7280'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Pie Chart */}
          <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
            <h2 className="font-semibold text-foreground mb-4">Status Distribution</h2>
            {pieData?.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No data available</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={({ status, percent }) => `${(percent * 100)?.toFixed(0)}%`}
                    labelLine={false}
                  >
                    {pieData?.map((entry) => (
                      <Cell key={entry?.status} fill={STATUS_COLORS_MAP?.[entry?.status] || '#6b7280'} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '12px', fontSize: '12px' }}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Status Summary Table */}
        <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="font-semibold text-foreground">Status Summary</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Status</th>
                <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Count</th>
                <th className="text-right px-4 py-3 font-semibold text-muted-foreground">% of Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {Object.entries(report?.byStatus || {})?.map(([status, count]) => (
                <tr key={status} className="hover:bg-muted/10 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS_MAP?.[status] || '#6b7280' }} />
                      <span className="text-foreground">{status}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-foreground">{count}</td>
                  <td className="px-4 py-3 text-right text-muted-foreground">
                    {report?.total > 0 ? ((count / report?.total) * 100)?.toFixed(1) : 0}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </MainLayout>
  );
};

export default IncentiveReports;
